import { Link } from "gatsby"
import React from "react"
import FeaturedMedia from "../../components/FeaturedMedia"
import Heading, { Eyebrow } from "../../components/Heading"
import BodyStyles from "../../components/BodyStyles"
import RichText from "../../components/RichText"
import SwitcherDifferentiator from "./SolutionDifferentiator"

export interface SolutionTileProps {
  /**
   * The title of the solution
   */
  eyebrow: string
  /**
   * The analytics title for the solution
   */
  analyticsTitle?: string
  /**
   * The quote or stats that are highlighted for the solution
   */
  differentiator: any
  /**
   * The heading for the differentiator
   */
  differentiatorHeading: string
  /**
   * The featured media from Kontent for the solution
   */
  featuredMedia: any
  /**
   * The headline for the solution from Kontent
   */
  heading: string
  /**
   *  The link object from Kontent
   */
  link?: any
  /**
   * Rich text from Kontent to provide an overview of the solution
   */
  overview: string
  /**
   * The slug that will be set in the client-facing UI.
   * This is set to the lowercased title if not passed in.
   */
  urlSlug?: string
  /**
   * modular content used in the overview
   */
  overviewLinkedItems?: any[]
  /**
   * items linked to
   */
  overviewLinks?: any[]
  /**
   * Additional classes to add to the container
   */
  className?: string
}

/**
 * A card with a featured media, text, and an optional differentiator.
 */
const SolutionTile = ({
  eyebrow,
  differentiator,
  differentiatorHeading,
  featuredMedia,
  heading,
  link,
  overview,
  overviewLinkedItems,
  overviewLinks,
  className,
}: SolutionTileProps) => {
  const { label: linkLabel, to: linkTo, ...linkProps } = link || {}
  return (
    <div className={className}>
      {featuredMedia && (
        <FeaturedMedia
          media={featuredMedia}
          cropRatio={0.5625}
          cropWidth={660}
        />
      )}
      <div className={`p-8 pb-12`}>
        <Heading eyebrow={eyebrow} text={heading} size="xl" />
        <BodyStyles className={`mt-4`} theme="compact">
          <RichText
            content={overview}
            linkedItems={overviewLinkedItems}
            links={overviewLinks}
          />
          {linkLabel ? (
            <p>
              {/* TODO: Make a `<MoreLink />` component */}
              <Link
                to={linkTo}
                className={`text-action-link hover:text-action font-normal focus:outline-none focus:ring transition-colors duration-150`}
                {...linkProps}
              >
                {linkLabel}
                <i
                  className="icon-chevron-right"
                  style={{ width: `1em`, marginRight: `-1em` }}
                ></i>
              </Link>
            </p>
          ) : null}
        </BodyStyles>
      </div>
      {differentiator && (
        <div
          className={`bg-gray-100 border-t border-gray-300 p-8 pb-12 space-y-2.5`}
        >
          {differentiatorHeading && (
            <Eyebrow as="h3">{differentiatorHeading}</Eyebrow>
          )}
          <div>
            <SwitcherDifferentiator differentiator={differentiator} />
          </div>
        </div>
      )}
    </div>
  )
}

export default SolutionTile
